import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { DataGrid } from '@mui/x-data-grid'
import { Box, Grid, Typography } from '../../../../../components/mui/Core/index'
import SummaryTablePagination from '../../../../../components/SummaryTablePagination/summary-table-pagination'
import LinkButton from '../../../../../components/LinkButton/link-button'
import { getNumberValueFormat } from '../../../../../utils/string-utils'
import { setSelectedOrder } from '../../../../outboundorders/Orders/order-slice'
import { setSelectedShipment } from '../../../../outboundorders/OrderList/order-list-slice'
import SubHeader from '../../../../../components/SubHeader/sub-header'
import Constants from '../../../../../constants/en.json'
import { sessionStorageKeyConstants } from '../../../../../constants/session-storage-key-constants'
import { useTheme } from '../../../../../components/mui/Styles/index'

export function OrderAllocationsSummaryTable() {
  const theme = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const orderAllocationSummary = useSelector(
    (state) => state.orderAllocationsSummary.orderAllocationsSummary,
  )
  const isFetchingOrderAllocationsSummary = useSelector(
    (state) => state.orderAllocationsSummary.isFetchingOrderAllocationsSummary,
  )
  const selectedClient = JSON.parse(
    sessionStorage.getItem(sessionStorageKeyConstants.SELECTED_CLIENT),
  )
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)
  const dmsUser = selectedClient?.dmsFlag || authenticatedUser?.dms
  const rows = orderAllocationSummary.map((row) => ({
    id: row.wmsOrder.orderNumber,
    totalQty: row.quantity,
    uom: row.uom,
    dmsOrderNumber: row.dmsOrder.orderNumber,
    wmsOrderNumber: row.wmsOrder.orderNumber,
    dmsOrderStatus: row.dmsOrder.orderStatus,
    wmsOrderStatus: row.wmsOrder.orderStatus,
  }))

  const handleDmsOrderLinkClick = (row) => {
    const selectedOrder = {
      dms_order: row.dmsOrderNumber,
      orderStatus: row.dmsOrderStatus,
    }
    dispatch(setSelectedOrder(selectedOrder))
    navigate('/orderlist')
  }

  const handleDmsShipmentLinkClick = (row) => {
    const selectedShipment = {
      dms_order: row.dmsOrderNumber,
      wmsOrderNum: row.wmsOrderNumber,
      orderStatus: row.wmsOrderStatus,
    }
    dispatch(setSelectedShipment(selectedShipment))
    navigate('/orderdetails')
  }

  const handleWmsOrderLinkClick = (row) => {
    const selectedShipment = {
      wms_order: row.wmsOrderNumber,
      orderStatus: row.wmsOrderStatus,
    }
    dispatch(setSelectedOrder(selectedShipment))
    navigate('/orderdetails')
  }
  const rowsRendered = rows.length > 5 ? rows.slice(0, 5) : rows

  /* DMS Columns */
  const dmsColumns = [
    {
      field: 'dmsOrderNumber',
      headerName: Constants.orderAllocations.ORDER,
      sortable: false,
      flex: 0.45,
      minWidth: 80,
      renderCell: (cellValues) => {
        return (
          <LinkButton
            handleLinkClick={() => {
              handleDmsOrderLinkClick(cellValues.row)
            }}
            linkText={cellValues.row.dmsOrderNumber}
          />
        )
      },
    },
    {
      field: 'wmsOrderNumer',
      headerName: Constants.orderAllocations.SHIPMENT,
      sortable: false,
      flex: 0.5,
      minWidth: 90,
      renderCell: (cellValues) => {
        return (
          <LinkButton
            handleLinkClick={() => {
              handleDmsShipmentLinkClick(cellValues.row)
            }}
            linkText={cellValues.row.wmsOrderNumber}
          />
        )
      },
    },
    {
      field: 'totalQty',
      headerName: Constants.orderAllocations.TOTAL_QTY,
      sortable: false,
      headerAlign: 'right',
      align: 'right',
      flex: 0.5,
      minWidth: 85,
      valueFormatter: (value) => {
        return getNumberValueFormat(value)
      },
    },
    {
      field: 'uom',
      headerName: Constants.orderAllocations.UOM,
      sortable: false,
      flex: 0.35,
      minWidth: 65,
    },
  ]

  /* WMS Columns */
  const wmsColumns = [
    {
      field: 'wmsOrderNumber',
      headerName: Constants.orderAllocations.ORDER,
      sortable: false,
      flex: 1,
      minWidth: 100,
      renderCell: (cellValues) => {
        return (
          <LinkButton
            handleLinkClick={() => {
              handleWmsOrderLinkClick(cellValues.row)
            }}
            linkText={cellValues.row?.wmsOrderNumber}
          />
        )
      },
    },
    {
      field: 'totalQty',
      headerName: Constants.orderAllocations.TOTAL_QTY,
      sortable: false,
      headerAlign: 'right',
      align: 'right',
      minWidth: 85,
      valueFormatter: (value) => {
        return getNumberValueFormat(value)
      },
    },
    {
      field: 'uom',
      headerName: Constants.orderAllocations.UOM,
      sortable: false,
      flex: 0.4,
      minWidth: 80,
    },
  ]

  const orderAllocationsSummaryTableStyle = {
    ...theme.dataGridMiniTable,
    minHeight: '160px',
    height: isFetchingOrderAllocationsSummary ? '400px' : 'auto',
  }

  return (
    <Box sx={{ flexGrow: 1, mb: 10 }}>
      <SubHeader title={Constants.orderAllocations.ORDER_ALLOCATIONS} />
      {!isFetchingOrderAllocationsSummary && rowsRendered.length === 0 ? (
        <Grid size={{ xs: 12 }} sx={{ px: 14, mt: 3 }}>
          <Typography variant='message' align='center'>
            {Constants.orderAllocations.THIS_ITEM_NOT_ALLOCATED}
          </Typography>
        </Grid>
      ) : (
        <DataGrid
          sx={orderAllocationsSummaryTableStyle}
          rows={rowsRendered}
          columns={dmsUser === 'Y' ? dmsColumns : wmsColumns}
          loading={isFetchingOrderAllocationsSummary}
          disableRowSelectionOnClick
          slots={{
            pagination: SummaryTablePagination,
          }}
          slotProps={{
            pagination: {
              totalRowsCount: rows.length,
              rowsRenderedCount: rowsRendered.length,
              route: '/orderallocations',
            },
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
          }}
        />
      )}
    </Box>
  )
}

export default OrderAllocationsSummaryTable
